/**
 * TreasuryVaultSection component for managing treasury GC and vault items.
 */

import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { Coins, Package, Plus, Trash2 } from 'lucide-react';
import {
  useWarbandStore,
  selectTreasury,
  selectVault,
  selectWarbandCost,
} from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TreasuryVaultSection() {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemNotes, setNewItemNotes] = useState('');

  // Store
  const treasuryGC = useWarbandStore(selectTreasury);
  const vault = useWarbandStore(selectVault);
  const warbandCost = useWarbandStore(selectWarbandCost);
  const setTreasuryGC = useWarbandStore((state) => state.setTreasuryGC);
  const addVaultItem = useWarbandStore((state) => state.addVaultItem);
  const removeVaultItem = useWarbandStore((state) => state.removeVaultItem);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleTreasuryChange = (value: string) => {
    const num = parseInt(value) || 0;
    setTreasuryGC(num);
  };

  const handleAddVaultItem = () => {
    if (!newItemName.trim()) return;

    addVaultItem({
      name: newItemName.trim(),
      notes: newItemNotes.trim() || undefined,
    });

    setNewItemName('');
    setNewItemNotes('');
    setIsAddingItem(false);
  };

  const handleRemoveVaultItem = (id: string) => {
    removeVaultItem(id);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-yellow-500" />
          <CardTitle>Treasury & Vault</CardTitle>
        </div>
        <CardDescription>
          Manage your gold crowns and stored items
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Treasury Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="treasury" className="flex items-center gap-2">
              <Coins className="h-4 w-4" />
              Treasury
            </Label>
            <span className="text-xs text-muted-foreground">
              Warband cost: {warbandCost}gc
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              id="treasury"
              type="number"
              min={0}
              value={treasuryGC}
              onChange={(e) => handleTreasuryChange(e.target.value)}
              className="max-w-[150px]"
            />
            <span className="text-sm text-muted-foreground">gold crowns</span>
          </div>
        </div>

        {/* Vault Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <Label>Vault ({vault.length} items)</Label>
          </div>

          {/* Vault Items */}
          {vault.length > 0 ? (
            <div className="space-y-2">
              {vault.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-md border border-border bg-muted/50 px-3 py-2"
                >
                  <div>
                    <span className="text-sm font-medium">{item.name}</span>
                    {item.notes && (
                      <p className="text-xs text-muted-foreground">{item.notes}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleRemoveVaultItem(item.id)}
                    aria-label={`Remove ${item.name} from vault`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-2 text-center text-sm text-muted-foreground">
              Vault is empty
            </p>
          )}

          {/* Add Item */}
          <Sheet open={isAddingItem} onOpenChange={setIsAddingItem}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setIsAddingItem(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add to Vault
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom">
              <SheetHeader>
                <SheetTitle>Add Item to Vault</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="vault-item-name">Item Name</Label>
                  <Input
                    id="vault-item-name"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Enter item name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vault-item-notes">Notes (optional)</Label>
                  <Input
                    id="vault-item-notes"
                    value={newItemNotes}
                    onChange={(e) => setNewItemNotes(e.target.value)}
                    placeholder="Add notes..."
                  />
                </div>
                <Button
                  onClick={handleAddVaultItem}
                  disabled={!newItemName.trim()}
                  className="w-full"
                >
                  Add to Vault
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </CardContent>
    </Card>
  );
}

